import { keyBy, sortBy, startCase } from 'lodash';
import React, { FC, useCallback, useState } from 'react';
import { Button } from 'components/atoms/button';
import { Filter } from 'components/molecules/filters/types';
import { isEmpty } from 'core/maybe';
import { useTranslation } from 'hooks/use-translation';
import { FilterableField } from '../fields';
import { AdvancedFilterComponent } from './advanced';
import { Box, Header, Switch } from './components';
import { FilterType } from './filter-type';
import { SimpleFilterComponent } from './simple';
import { SimpleFilter } from './simple-filter';

interface Props {
  fields: FilterableField[];
  filter: Filter;
  onChange: (filter: Filter) => void;
}

export const FilterBox: FC<Props> = ({ fields, filter, onChange }) => {
  const lookup = keyBy(fields, 'fieldName');

  const [advanced, setAdvanced] = useState(
    isEmpty(SimpleFilter.tryParse(filter, lookup)),
  );
  const sorted = sortBy(
    fields,
    ({ fieldLabel, fieldName }) => fieldLabel ?? startCase(fieldName),
  );
  const switchMode = useCallback(() => {
    if (!advanced) {
      setAdvanced(true);
    } else {
      onChange(
        SimpleFilter.toFilter(lookup)(SimpleFilter.parse(filter, lookup)),
      );
      setAdvanced(false);
    }
  }, [advanced, setAdvanced, filter, onChange, lookup]);
  const mode = advanced ? 'simple' : 'advanced';
  const caption = useTranslation('task_list.filters.switch_mode', { mode });

  return (
    <Box className="filter-box" data-cy="task.filter-popover">
      <Header>
        <FilterType
          readonly={!advanced}
          value={filter.type}
          onChange={(type) => onChange({ ...filter, type })}
        />
        <Switch>
          <Button
            data-cy={`buttons.switch-${mode}`}
            type="link"
            onClick={switchMode}
          >
            {caption}
          </Button>
        </Switch>
      </Header>
      {advanced ? (
        <AdvancedFilterComponent
          fields={sorted}
          filter={filter}
          onChange={onChange}
        />
      ) : (
        <SimpleFilterComponent
          lookup={lookup}
          filter={filter}
          onChange={onChange}
        />
      )}
    </Box>
  );
};
